import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Settings, Users, FileText, Send, BarChart3, UserPlus, UserX, Shield, ShieldCheck,
  Loader2, Upload, Search, X, Check, Trash2, Edit, Ban, Unlock, TrendingUp,
  MessageSquare, Calendar, Clock, Activity, Mail, Briefcase, Receipt, FolderOpen, Eye
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'contracts', label: 'Contrats', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  { value: 'payslips', label: 'Fiches de paie', icon: Receipt, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'leave', label: 'Congés', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
  { value: 'other', label: 'Autres', icon: FolderOpen, color: 'bg-purple-100 text-purple-700' }
];

// Configuration des applications disponibles pour la personnalisation
const AVAILABLE_APPS = [
  { key: 'dashboard', label: 'Tableau de bord', category: 'Principal' },
  { key: 'planning', label: 'Planification', category: 'Organisation' },
  { key: 'time-tracking', label: 'Suivi du temps', category: 'Organisation' },
  { key: 'forms', label: 'Listes de contrôle', category: 'Organisation' },
  { key: 'tasks', label: 'Gestion des Tâches', category: 'Organisation' },
  { key: 'messages', label: 'Messages', category: 'Communication' },
  { key: 'chat', label: 'Chat', category: 'Communication' },
  { key: 'news', label: 'Actualités', category: 'Communication' },
  { key: 'directory', label: 'Annuaire', category: 'Communication' },
  { key: 'knowledge', label: 'Méthodes', category: 'Communication' },
  { key: 'support', label: 'Assistance technique', category: 'Communication' },
  { key: 'events', label: 'Événements', category: 'Communication' },
  { key: 'training', label: 'Formation', category: 'Espace RH' },
  { key: 'documents', label: 'Documents', category: 'Espace RH' },
  { key: 'recognition', label: 'Reconnaissance', category: 'Espace RH' },
  { key: 'leaves', label: 'Congés', category: 'Espace RH' },
  { key: 'notifications', label: 'Notifications', category: 'Système' },
];

export default function AdminPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Users management
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToBlock, setUserToBlock] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    department: ''
  });
  
  // Send document
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [sendForm, setSendForm] = useState({ name: '', category: '', description: '', file: null });

  // Visible apps configuration
  const [appsDialogOpen, setAppsDialogOpen] = useState(false);
  const [selectedUserForApps, setSelectedUserForApps] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [savingApps, setSavingApps] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/users`, { headers })
      ]);
      
      if (statsRes.data) setStats(statsRes.data);
      setUsers(usersRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(searchUser.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Users for recipient selection (exclude current user)
  const recipientUsers = allUsers.filter(u => 
    u.id !== user?.id && 
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(recipientSearch.toLowerCase())
  );

  // User management functions
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilisateur supprimé');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleBlockUser = async () => {
    if (!userToBlock) return;
    const action = userToBlock.status === 'blocked' ? 'unblock' : 'block';
    try {
      await axios.put(`${API_URL}/api/users/${userToBlock.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'block' ? 'Utilisateur bloqué' : 'Utilisateur débloqué');
      setBlockDialogOpen(false);
      setUserToBlock(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditDialog = (userItem) => {
    setUserToEdit(userItem);
    setEditForm({
      first_name: userItem.first_name || '',
      last_name: userItem.last_name || '',
      email: userItem.email || '',
      role: userItem.role || 'employee',
      department: userItem.department || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!userToEdit) return;
    
    if (!editForm.first_name || !editForm.last_name || !editForm.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/users/${userToEdit.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilisateur modifié avec succès');
      setEditDialogOpen(false);
      setUserToEdit(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve/Reject pending user
  const handleApproveUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/activate`, { action: 'approve' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilisateur approuvé avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/activate`, { action: 'reject' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande refusée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  // Send document functions
  const handleSendFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10MB');
      return;
    }
    setSendForm({ ...sendForm, file });
  };

  const handleSendDocument = async (e) => {
    e.preventDefault();
    if (!sendForm.name || !sendForm.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!sendToAll && selectedRecipients.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    if (!sendForm.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setSubmitting(true);
    try {
      let fileData = null, fileType = null;
      if (sendForm.file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(sendForm.file);
        });
        fileType = sendForm.file.type;
      }

      await axios.post(`${API_URL}/api/documents/send`, {
        name: sendForm.name,
        category: sendForm.category,
        description: sendForm.description,
        file_data: fileData,
        file_type: fileType,
        recipients: selectedRecipients.map(u => u.id),
        send_to_all: sendToAll
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recipientCount = sendToAll ? recipientUsers.length : selectedRecipients.length;
      toast.success(`Document envoyé à ${recipientCount} destinataire(s)`);
      setSendDialogOpen(false);
      setSendForm({ name: '', category: '', description: '', file: null });
      setSelectedRecipients([]);
      setSendToAll(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRecipient = (userItem) => {
    if (selectedRecipients.find(r => r.id === userItem.id)) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== userItem.id));
    } else {
      setSelectedRecipients([...selectedRecipients, userItem]);
    }
  };

  // Visible apps functions
  const openAppsDialog = async (userItem) => {
    setSelectedUserForApps(userItem);
    try {
      const res = await axios.get(`${API_URL}/api/users/${userItem.id}/visible-apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.is_admin) {
        toast.info('Les administrateurs ont accès à toutes les applications');
        return;
      }
      setSelectedApps(res.data.visible_apps || []);
      setAppsDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des permissions');
    }
  };

  const handleAppToggle = (appKey) => {
    setSelectedApps(prev => {
      if (prev.includes(appKey)) {
        return prev.filter(a => a !== appKey);
      } else {
        return [...prev, appKey];
      }
    });
  };

  const handleSaveApps = async () => {
    if (!selectedUserForApps) return;
    setSavingApps(true);
    try {
      await axios.put(`${API_URL}/api/users/${selectedUserForApps.id}/visible-apps`, 
        { visible_apps: selectedApps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Permissions mises à jour avec succès');
      setAppsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingApps(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-amber-100 text-amber-800"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>;
    }
    return <Badge variant="secondary"><Shield className="w-3 h-3 mr-1" />Employé</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'blocked') {
      return <Badge variant="destructive">Bloqué</Badge>;
    }
    if (status === 'pending') {
      return <Badge variant="outline" className="text-amber-600 border-amber-300">En attente</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-800">Actif</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Administration
          </h1>
          <p className="text-slate-500 mt-1">Gérez les utilisateurs et les documents</p>
        </div>
        <Button 
          onClick={() => setSendDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="send-document-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Envoyer un document
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Tableau de bord</span>
            <span className="sm:hidden">Stats</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Utilisateurs</span>
            <span className="sm:hidden">Users</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="tab-documents">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Documents envoyés</span>
            <span className="sm:hidden">Docs</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Utilisateurs</p>
                    <p className="text-3xl font-bold text-slate-900">{users.length}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-emerald-600 font-medium">
                    {users.filter(u => u.status === 'active').length} actifs
                  </span>
                  <span className="text-slate-400">•</span>
                  <span className="text-amber-600">
                    {users.filter(u => u.status === 'pending').length} en attente
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Admins</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {users.filter(u => u.role === 'admin').length}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <ShieldCheck className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Administrateurs
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Employés</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {users.filter(u => u.role === 'employee').length}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Membres de l'équipe
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Bloqués</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {users.filter(u => u.status === 'blocked').length}
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-xl">
                    <Ban className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Comptes suspendus
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Derniers utilisateurs inscrits
              </CardTitle>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Aucun utilisateur</p>
              ) : (
                <div className="space-y-3">
                  {users.slice(0, 5).map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-slate-200">
                            {getInitials(u.first_name, u.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{u.first_name} {u.last_name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(u.role)}
                        {getStatusBadge(u.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    placeholder="Rechercher un utilisateur..."
                    className="pl-10"
                  />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filtrer par rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les rôles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="employee">Employé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>Liste des utilisateurs ({filteredUsers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                          Aucun utilisateur trouvé
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-slate-200 text-xs">
                                  {getInitials(u.first_name, u.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{u.first_name} {u.last_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500">{u.email}</TableCell>
                          <TableCell>{getRoleBadge(u.role)}</TableCell>
                          <TableCell>{getStatusBadge(u.status)}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {u.status === 'pending' ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => handleApproveUser(u.id)}
                                    title="Approuver"
                                    data-testid={`approve-user-${u.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleRejectUser(u.id)}
                                    title="Refuser"
                                    data-testid={`reject-user-${u.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {u.role === 'employee' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                      onClick={() => openAppsDialog(u)}
                                      title="Configurer les applications visibles"
                                      data-testid={`apps-user-${u.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:text-blue-700"
                                    onClick={() => openEditDialog(u)}
                                    disabled={u.id === user?.id}
                                    title="Modifier"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={u.status === 'blocked' 
                                      ? "text-green-600 hover:text-green-700" 
                                      : "text-orange-600 hover:text-orange-700"
                                    }
                                    onClick={() => { setUserToBlock(u); setBlockDialogOpen(true); }}
                                    disabled={u.id === user?.id}
                                    title={u.status === 'blocked' ? 'Débloquer' : 'Bloquer'}
                                  >
                                    {u.status === 'blocked' ? <Unlock className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                                    disabled={u.id === user?.id}
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents envoyés
              </CardTitle>
              <CardDescription>
                Historique des documents envoyés aux utilisateurs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-400">
                <Send className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">Envoyez votre premier document</p>
                <p className="text-sm mt-1">Cliquez sur "Envoyer un document" pour commencer</p>
                <Button 
                  onClick={() => setSendDialogOpen(true)}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer un document
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer l'utilisateur</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong> ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block User Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {userToBlock?.status === 'blocked' ? 'Débloquer l\'utilisateur' : 'Bloquer l\'utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {userToBlock?.status === 'blocked' 
                ? `Voulez-vous débloquer ${userToBlock?.first_name} ${userToBlock?.last_name} ? L'utilisateur pourra de nouveau se connecter.`
                : `Voulez-vous bloquer ${userToBlock?.first_name} ${userToBlock?.last_name} ? L'utilisateur ne pourra plus se connecter.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>Annuler</Button>
            <Button 
              onClick={handleBlockUser}
              className={userToBlock?.status === 'blocked' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              {userToBlock?.status === 'blocked' ? (
                <><Unlock className="w-4 h-4 mr-2" />Débloquer</>
              ) : (
                <><Ban className="w-4 h-4 mr-2" />Bloquer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setUserToEdit(null);
          setEditForm({ first_name: '', last_name: '', email: '', role: '', department: '' });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Modifier l'utilisateur
            </DialogTitle>
            <DialogDescription>
              Modifiez les informations de {userToEdit?.first_name} {userToEdit?.last_name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  placeholder="Prénom"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  placeholder="Nom"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="email@exemple.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select 
                  value={editForm.role} 
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employé</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Département</Label>
                <Input
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  placeholder="Ex: Marketing"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Enregistrer</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Document Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={(open) => {
        setSendDialogOpen(open);
        if (!open) {
          setSendForm({ name: '', category: '', description: '', file: null });
          setSelectedRecipients([]);
          setSendToAll(false);
          setRecipientSearch('');
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Envoyer un document
            </DialogTitle>
            <DialogDescription>
              Envoyez un document à un ou plusieurs utilisateurs
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du document *</Label>
              <Input
                value={sendForm.name}
                onChange={(e) => setSendForm({ ...sendForm, name: e.target.value })}
                placeholder="Ex: Fiche de paie - Janvier 2026"
              />
            </div>

            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select value={sendForm.category} onValueChange={(v) => setSendForm({ ...sendForm, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={sendForm.description}
                onChange={(e) => setSendForm({ ...sendForm, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Fichier *</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-slate-300 transition-colors">
                <input type="file" onChange={handleSendFileChange} className="hidden" id="send-file-input" />
                <label htmlFor="send-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  {sendForm.file ? (
                    <p className="text-sm text-slate-700 font-medium">{sendForm.file.name}</p>
                  ) : (
                    <>
                      <p className="text-sm text-slate-600">Cliquez pour sélectionner un fichier</p>
                      <p className="text-xs text-slate-400 mt-1">Max. 10MB</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Recipients selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Destinataires *</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="send-to-all"
                    checked={sendToAll}
                    onCheckedChange={(checked) => {
                      setSendToAll(checked);
                      if (checked) setSelectedRecipients([]);
                    }}
                  />
                  <label htmlFor="send-to-all" className="text-sm text-slate-600 cursor-pointer">
                    Envoyer à tous
                  </label>
                </div>
              </div>

              {!sendToAll && (
                <>
                  {selectedRecipients.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded-lg">
                      {selectedRecipients.map((recipient) => (
                        <Badge key={recipient.id} variant="secondary" className="flex items-center gap-1 pr-1 bg-white">
                          <span>{recipient.first_name} {recipient.last_name}</span>
                          <button type="button" onClick={() => toggleRecipient(recipient)} className="ml-1 hover:bg-slate-200 rounded-full p-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} placeholder="Rechercher un utilisateur..." className="pl-10" />
                  </div>

                  <ScrollArea className="h-48 border rounded-lg">
                    {recipientUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Aucun utilisateur trouvé</p>
                    ) : (
                      <div className="p-2 space-y-1">
                        {recipientUsers.map((u) => {
                          const isSelected = selectedRecipients.some(r => r.id === u.id);
                          return (
                            <div
                              key={u.id}
                              onClick={() => toggleRecipient(u)}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-blue-100' : 'hover:bg-slate-100'}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className={isSelected ? 'bg-blue-200' : 'bg-slate-200'}>
                                  {getInitials(u.first_name, u.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{u.first_name} {u.last_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                              </div>
                              {getRoleBadge(u.role)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </>
              )}

              {sendToAll && (
                <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-700">Le document sera envoyé à {recipientUsers.length} utilisateur(s)</span>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSendDialogOpen(false)}>Annuler</Button>
              <Button 
                type="submit" 
                disabled={submitting || (!sendToAll && selectedRecipients.length === 0)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Envoyer ({sendToAll ? recipientUsers.length : selectedRecipients.length})</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visible Apps Configuration Dialog */}
      <Dialog open={appsDialogOpen} onOpenChange={setAppsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Applications visibles
            </DialogTitle>
            <DialogDescription>
              Configurer les applications accessibles pour{' '}
              <strong>{selectedUserForApps?.first_name} {selectedUserForApps?.last_name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {['Principal', 'Organisation', 'Communication', 'Espace RH', 'Système'].map(category => {
              const categoryApps = AVAILABLE_APPS.filter(app => app.category === category);
              if (categoryApps.length === 0) return null;
              
              return (
                <div key={category} className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">{category}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {categoryApps.map(app => (
                      <label
                        key={app.key}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          id={`app-${app.key}`}
                          checked={selectedApps.includes(app.key)}
                          onCheckedChange={() => handleAppToggle(app.key)}
                          data-testid={`checkbox-${app.key}`}
                        />
                        <span className="text-sm">{app.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppsDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveApps} 
              disabled={savingApps}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-visible-apps"
            >
              {savingApps ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" />Enregistrer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
